import { BaseMessage } from '~/utils/baseMessage'
import axios from 'axios'
import { useUserStore, IMS_DEV_MODE } from '~/store/common/user'
import { useAuthLinks } from '~/composables/useAuthLinks'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import { getAuthCookieOptions } from '~/utils/storageKey'
import {
  clearSensitivePublicKeyCache,
  decryptSensitiveResponse,
  isSensitiveEnvelope,
  prepareSensitiveRequest,
} from '~/utils/sensitive-api'
import { shouldForwardSsrAuth } from '~/utils/website/ssrRequestPolicy'

/**
 * @description 全局 Axios 实例，包含错误拦截器和认证处理
 * @property {boolean} config.rawResponse - 是否返回原始报文且不进行自动错误提示。
 * @property {boolean} [config.showError] - 是否在失败时弹出错误提示。默认 true（表单/搜索等主动操作必须有提示）。
 *  传 false 时静默失败，用于页面自动加载的列表、下拉数据等，由业务决定显示空状态或省略模块。
 * @property {string} [config.concurrencyKey] - 防竞态键。同 key 的新请求会取消未完成的旧请求，仅保留最新结果。
 * @property {string|string[]} [config.cancelKeys] - 发起请求时需要主动取消的其他 concurrencyKey（有序数组）。用于联动场景。
 * 有序取消规则：当前请求只会取消 cancelKeys 中排在它后面的请求（包括自己），不影响排在它前面的请求。
 * @example
 * // 默认模式：返回 res.data，自动提示错误
 * const data = await api.get('/user')
 *
 * // 页面静默加载：失败不弹窗，由业务显示空状态
 * const data = await api.get('/list', { showError: false })
 *
 * // 透明模式：返回 axios response，需自行处理错误
 * const response = await api.get('/user', { rawResponse: true })
 *
 * // 防竞态：搜索联想等场景，仅处理最新请求
 * const data = await api.get('/search', { params: { q } }, { concurrencyKey: 'search' })
 *
 * // 有序取消：A、B、C 三个请求都设置 cancelKeys: ['A', 'B', 'C']
 * // - A 重新请求时，取消旧的 A、B、C
 * // - B 重新请求时，只取消旧的 B、C（不影响 A）
 * // - C 重新请求时，只取消旧的 C（不影响 A、B）
 * const dataA = await api.get('/api/a', { concurrencyKey: 'A', cancelKeys: ['A', 'B', 'C'] })
 * const dataB = await api.get('/api/b', { concurrencyKey: 'B', cancelKeys: ['A', 'B', 'C'] })
 * const dataC = await api.get('/api/c', { concurrencyKey: 'C', cancelKeys: ['A', 'B', 'C'] })
 */

/** 错误提示去重：短时间内多次失败只提示一次（避免网络异常时刷屏） */
const ERROR_MESSAGE_DEBOUNCE_MS = 2500
let lastErrorMessageTime = 0

function shouldShowError(config) {
  return config?.showError !== false
}

function showErrorMessage(message, type = 'error') {
  const now = Date.now()
  if (now - lastErrorMessageTime < ERROR_MESSAGE_DEBOUNCE_MS) return
  lastErrorMessageTime = now
  BaseMessage[type === 'warning' ? 'warning' : 'error'](message)
}

// 根据环境设置默认 baseURL
const defaultBaseURL = import.meta.dev ? '/replaceApi' : '/tdc/api'

/**
 * @description Resolve client-side API baseURL without relying on window.__NUXT__ timing
 * @returns {string}
 */
function resolveClientApiBaseURL() {
  try {
    const runtimeConfig = useRuntimeConfig()
    const env = runtimeConfig.public?.env || (import.meta.dev ? 'development' : 'production')
    const devProxyPrefix = runtimeConfig.public?.viteAppBaseApi || '/replaceApi'

    if (import.meta.dev) {
      // 本地 dev（包括 npm run dev:test）：浏览器端必须走同源代理，避免直连测试域名触发 CORS。
      return devProxyPrefix
    }

    if (env === 'production') {
      // 生产环境：走 Nginx 反向代理
      return '/tdc/api'
    }

    // 测试/预发环境：直接请求完整域名
    const apiBase = runtimeConfig.public?.apiBase
    if (apiBase && apiBase.startsWith('http')) {
      return apiBase
    }

    return '/tdc/api'
  } catch (e) {
    return import.meta.dev ? '/replaceApi' : '/tdc/api'
  }
}

const api = axios.create({
  baseURL: defaultBaseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Locale: 'zh_CN',
  },
})

// Token 刷新队列，避免多个请求同时触发刷新
let isRefreshing = false
let refreshSubscribers = []

// IMPORTANT: 这是模块级注册表。公开业务请求当前由浏览器持有，因此每个页面运行时相互隔离；
// 不要直接将带 concurrencyKey 的公开请求迁回 SSR，共享 Node worker 会让不同访客互相取消。
// 若确需业务数据 SSR，必须先改为按本次 SSR 请求隔离的注册表，并完成并发压测。
/** @type {Record<string, { controller: AbortController; requestId: string }>} */
const pendingConcurrencyRequests = {}

/**
 * @description 生成防竞态请求唯一 ID
 * @returns {string}
 */
function nextConcurrencyRequestId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * @description 清理防竞态记录（仅当该请求仍为当前 key 的活跃请求时删除）
 * @param {import('axios').InternalAxiosRequestConfig} [config]
 */
function clearConcurrencyEntry(config) {
  const key = config?.concurrencyKey
  if (!key || typeof key !== 'string') return
  const cur = pendingConcurrencyRequests[key]
  if (cur && cur.requestId === config._concurrencyRequestId) {
    delete pendingConcurrencyRequests[key]
  }
}

/**
 * @description 根据有序 cancelKeys 规则确定要取消的 keys
 * @param {string} [concurrencyKey] - 当前请求的 concurrencyKey
 * @param {string|string[]} [cancelKeys] - 有序的 cancelKeys 数组
 * @returns {string[]} 需要取消的 keys 数组
 * @reason 实现有序取消：只取消 cancelKeys 中排在当前 concurrencyKey 后面的请求（包括自己）
 */
function getKeysToCancel(concurrencyKey, cancelKeys) {
  const keysToCancel = []

  // 处理有序 cancelKeys：只取消排在当前 concurrencyKey 后面的请求（包括自己）
  if (cancelKeys && concurrencyKey && typeof concurrencyKey === 'string') {
    let cancelKeysArray = []
    if (Array.isArray(cancelKeys)) {
      cancelKeysArray = cancelKeys.filter((k) => k && typeof k === 'string')
    } else if (typeof cancelKeys === 'string') {
      cancelKeysArray = [cancelKeys]
    }

    // 找到当前 concurrencyKey 在 cancelKeys 中的位置
    const currentIndex = cancelKeysArray.indexOf(concurrencyKey)

    if (currentIndex !== -1) {
      // 只取消从当前位置开始到数组末尾的所有 key（包括当前 key）
      keysToCancel.push(...cancelKeysArray.slice(currentIndex))
    } else {
      // 如果当前 concurrencyKey 不在 cancelKeys 中，只取消自己的旧请求
      keysToCancel.push(concurrencyKey)
    }
  } else if (concurrencyKey && typeof concurrencyKey === 'string') {
    // 没有 cancelKeys 时，只取消自己的旧请求
    keysToCancel.push(concurrencyKey)
  } else if (cancelKeys) {
    // 有 cancelKeys 但没有 concurrencyKey 时，取消所有 cancelKeys（向后兼容）
    if (Array.isArray(cancelKeys)) {
      keysToCancel.push(...cancelKeys.filter((k) => k && typeof k === 'string'))
    } else if (typeof cancelKeys === 'string') {
      keysToCancel.push(cancelKeys)
    }
  }

  return keysToCancel
}

/**
 * @description 取消指定的防竞态请求
 * @param {string[]} keys - 需要取消的 concurrencyKey 数组
 */
function cancelConcurrencyRequests(keys) {
  keys.forEach((key) => {
    const prev = pendingConcurrencyRequests[key]
    if (prev) {
      prev.controller.abort()
      delete pendingConcurrencyRequests[key]
    }
  })
}

/**
 * @description 注册当前请求的防竞态控制
 * @param {import('axios').InternalAxiosRequestConfig} config - 请求配置
 * @param {string} concurrencyKey - 防竞态键
 */
function registerConcurrencyRequest(config, concurrencyKey) {
  if (concurrencyKey && typeof concurrencyKey === 'string' && !config.signal) {
    const controller = new AbortController()
    const requestId = nextConcurrencyRequestId()
    config.signal = controller.signal
    config._concurrencyRequestId = requestId
    pendingConcurrencyRequests[concurrencyKey] = { controller, requestId }
  }
}

/**
 * @description 添加刷新订阅者（等待 token 刷新完成的请求）
 * @param {Function} resolve - 刷新成功回调函数
 * @param {Function} reject - 刷新失败回调函数
 * @returns {void}
 */
function subscribeTokenRefresh(resolve, reject) {
  refreshSubscribers.push({ resolve, reject })
}

/**
 * @description 通知所有订阅者 token 已刷新
 * @returns {void}
 */
function onTokenRefreshed() {
  refreshSubscribers.forEach(({ resolve }) => resolve())
  refreshSubscribers = []
}

/**
 * @description 通知所有订阅者 token 刷新失败
 * @param {Error} error - token 刷新失败原因
 * @returns {void}
 */
function onTokenRefreshFailed(error) {
  refreshSubscribers.forEach(({ reject }) => reject(error))
  refreshSubscribers = []
}

/**
 * @description 处理 token 刷新
 * @param {Object} [options={}] - 刷新选项
 * @param {boolean} [options.force=false] - 是否跳过本地有效期判断并强制刷新
 * @returns {Promise<void>}
 */
async function handleTokenRefresh({ force = false } = {}) {
  if (isRefreshing) {
    // 如果正在刷新，等待刷新完成
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh(resolve, reject)
    })
  }

  // 检查是否已经有有效的 token，如果有且未过期，不需要刷新
  if (import.meta.client) {
    try {
      const userStore = useUserStore()
      // 如果 token 存在且未过期，直接返回
      if (!force && userStore.accessToken && !userStore.shouldRefreshAccessToken) {
        console.warn('[API] Token 仍然有效，无需刷新')
        return
      }
    } catch (e) {
      // 忽略错误，继续刷新流程
    }
  }

  isRefreshing = true

  try {
    if (import.meta.client) {
      const userStore = useUserStore()
      await userStore.refreshAccessToken({ force })
      onTokenRefreshed()
      isRefreshing = false
    } else {
      isRefreshing = false
    }
  } catch (error) {
    // 刷新失败，清除所有订阅者并跳转登录
    onTokenRefreshFailed(error)
    isRefreshing = false

    if (import.meta.client) {
      // 刷新失败，统一处理认证失效跳转
      const { handleUnauthorized } = useAuthLinks()
      await handleUnauthorized({ forceRedirect: true, redirectToLogin: true })
    }

    throw error
  }
}

// 请求拦截器
api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {}

    // 可以在这里根据环境动态修改 baseURL
    if (import.meta.server) {
      // 服务端请求：开发环境走同源代理（与浏览器端保持一致），避免 SSR 直连外网/不存在的路径
      // @reason 本地只配置了 /replaceApi 代理，SSR 若默认拼 /c 会导致详情页刷新接口失败
      // @reason 生产优先 NUXT_SSR_API_BASE（如 http://127.0.0.1/tdc/api），避免回环公网 hairpin
      let apiBase = ''
      try {
        const runtimeConfig = useRuntimeConfig()
        const env =
          runtimeConfig.public?.env ||
          process.env.NUXT_PUBLIC_ENV ||
          (process.env.NODE_ENV === 'production' ? 'production' : 'development')
        const devProxyPrefix = runtimeConfig.public?.viteAppBaseApi || '/replaceApi'
        const ssrApiBase = String(
          runtimeConfig.ssrApiBase || process.env.NUXT_SSR_API_BASE || ''
        ).trim()
        const configuredApiBase =
          runtimeConfig.public?.apiBase || process.env.NUXT_PUBLIC_API_BASE || ''

        const url = useRequestURL()

        if (import.meta.dev || env === 'development') {
          apiBase = `${url.origin}${devProxyPrefix}`
        } else if (ssrApiBase) {
          apiBase = ssrApiBase
        } else if (configuredApiBase) {
          apiBase = configuredApiBase.startsWith('/')
            ? `${url.origin}${configuredApiBase}`
            : configuredApiBase
        } else {
          apiBase = `${url.origin}/tdc/api`
        }
      } catch (e) {
        apiBase =
          process.env.NUXT_SSR_API_BASE ||
          process.env.NUXT_PUBLIC_API_BASE ||
          'https://testapp.ip-com.com.cn/tdc/api'
      }

      config.baseURL = String(apiBase).trim()
    } else {
      config.baseURL = resolveClientApiBaseURL()
    }

    try {
      // SSR 下无法读取 localStorage 的 pinia 持久化数据，因此需要从 Cookie 恢复 token
      // @reason 商品详情等接口在未携带 token 时会返回 A00005，导致 SSR 首屏数据为空
      if (import.meta.server && shouldForwardSsrAuth(config)) {
        const keys = getRuntimeAuthKeys()
        const tokenCookie = useCookie(keys.accessTokenCookie, { readonly: true })
        if (tokenCookie.value) {
          config.headers['opentoken'] = tokenCookie.value
        }
      }

      // 添加认证 token - 使用 Nuxt 的 useCookie
      // 注意：在某些异步上下文中 useCookie 可能会失效，建议在插件中全局注入
      // const tokenCookie = useCookie('ims-access-token', { readonly: true })
      // if (tokenCookie.value) {
      //   config.headers['open-token'] = `Bearer ${tokenCookie.value}`
      // }
      // 添加认证 token - 从 user store 获取 access_token
      if (import.meta.client) {
        const userStore = useUserStore()
        const keys = getRuntimeAuthKeys()
        const tokenCookie = useCookie(keys.accessTokenCookie, { readonly: true })
        userStore.restoreImsTokenFromCookies()

        // 主动检查 token 状态（仅在需要 token 的请求中）
        // 如果 token 已过期或即将过期，先尝试刷新
        if (userStore.isLoggedIn && userStore.refreshToken) {
          // 检查 refresh_token 是否过期
          if (userStore.isRefreshTokenExpired) {
            // refresh_token 已过期，标记请求不需要重试，统一处理认证失效跳转
            config._skipRetry = true
            const { handleUnauthorized } = useAuthLinks()
            await handleUnauthorized({ forceRedirect: true, redirectToLogin: true })
            return Promise.reject(new Error('refresh_token 已过期，请重新登录'))
          }

          // 如果 access_token 已进入 IMS 约定的到期前 60 秒刷新窗口，主动刷新
          if (userStore.shouldRefreshAccessToken) {
            // 等待刷新完成后再发起当前请求，避免关键接口携带旧 opentoken
            await handleTokenRefresh()
          }
        }

        // 添加 token 到请求头
        const token = userStore.accessToken || tokenCookie.value
        if (token) {
          config.headers['opentoken'] = token
        }
      }
      // user.js 中已经处理了开发模式下的 token 注入
      // 本地开发环境注入特定 token
      // if (0 && import.meta.dev) {
      // 2026-03-15之前有效
      // if (IMS_DEV_MODE && new Date().getTime() < 1773619200000) {
      //   // 如果 store 中没有 token，则尝试获取
      //   const devToken = userStore.accessToken
      //   if (devToken) {
      //     config.headers['opentoken'] = devToken

      //     // 如果用户信息为空，则触发获取
      //     if (!userStore.userInfo && import.meta.client) {
      //       userStore.fetchUserInfo().catch(() => {})
      //     }
      //   }
      // }
    } catch (e) {
      // 静默失败
    }

    // 处理防竞态逻辑
    const concurrencyKey = config.concurrencyKey
    const cancelKeys = config.cancelKeys
    const keysToCancel = getKeysToCancel(concurrencyKey, cancelKeys)
    cancelConcurrencyRequests(keysToCancel)

    if (import.meta.client) {
      if (!Object.prototype.hasOwnProperty.call(config, '__rawData')) {
        config.__rawData = config.data
      }

      const sensitiveRequest = await prepareSensitiveRequest({
        url: config.url,
        method: config.method,
        data: config.__rawData,
        apiBase: config.baseURL,
        headers: config.headers,
      })

      config.__sensitiveApi = sensitiveRequest.sensitiveApi
      config.__sensitiveContext = sensitiveRequest.context || null

      if (sensitiveRequest.sensitiveApi?.requestSensitive) {
        config.data = sensitiveRequest.data
      }

      if (sensitiveRequest.headers && Object.keys(sensitiveRequest.headers).length > 0) {
        config.headers = {
          ...config.headers,
          ...sensitiveRequest.headers,
        }
      }
    }

    registerConcurrencyRequest(config, concurrencyKey)

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  async (response) => {
    const { config } = response
    let res = response.data

    clearConcurrencyEntry(config)

    if (
      import.meta.client &&
      config.__sensitiveApi?.responseSensitive &&
      isSensitiveEnvelope(res)
    ) {
      res = await decryptSensitiveResponse(res, config.__sensitiveContext)
      response.data = res
    }

    if (config.rawResponse) {
      return response
    }

    // 默认模式：处理业务逻辑错误
    // 支持两种响应格式：
    // 1. { success: true/false, data: {}, message: '' }
    // 2. { code: 200, msg: "success", data: {} }

    // 特殊处理：检测 token 过期
    // 优先检查 code === "A00004" 或 message 包含 "Unauthorized"
    const message = res.message || res.msg || ''
    if (res.code === 'A00004' || message.includes('Unauthorized')) {
      // Token 过期，需要刷新 token 后重试请求
      return handleTokenExpired(config)
    }

    // 敏感接口密钥版本不支持，清除公钥缓存后重试一次
    if (res.code === 'A00903' && !config._sensitiveKeyRetry) {
      config._sensitiveKeyRetry = true
      clearSensitivePublicKeyCache()
      return api(config)
    }

    if (res.success === false) {
      const errorMsg = message || '请求失败'
      if (import.meta.client && shouldShowError(config)) {
        showErrorMessage(errorMsg)
      }
      const err = new Error(errorMsg)
      if (res.code !== undefined) {
        err.code = res.code
      }
      err.raw = res
      return Promise.reject(err)
    }

    // 处理 code 格式的响应（如用户信息接口）
    if (res.code !== undefined && String(res.code) !== '00000') {
      const bizCode = String(res.code)
      const allowBizCodes = config?.allowBizCodes
      const isAllowedBizCode = Array.isArray(allowBizCodes)
        ? allowBizCodes.map((c) => String(c)).includes(bizCode)
        : typeof allowBizCodes === 'string'
          ? String(allowBizCodes) === bizCode
          : false

      if (!isAllowedBizCode) {
        // 检测 token 过期：code === "A00004"
        if (bizCode === 'A00004') {
          // Token 过期，需要刷新 token 后重试请求
          return handleTokenExpired(config)
        }

        // 其他错误码处理
        if (
          bizCode === 'A04001' ||
          bizCode === 'A00001' ||
          bizCode === 'A00012' ||
          bizCode === 'A00006' ||
          bizCode === 'A00005' ||
          bizCode === 'A00007' ||
          bizCode === 'A00014'
        ) {
          if (import.meta.client && shouldShowError(config)) {
            showErrorMessage(res.msg || '请求失败')
          }
          const err = new Error(res.msg || `请求失败，错误码: ${bizCode}`)
          err.code = bizCode
          err.raw = res
          return Promise.reject(err)
        }
      }
    }

    // 返回 data 字段，如果不存在则返回整个响应
    // @reason 确保 data 即使为 null 也被返回（由业务逻辑处理），而不是退回到整个响应对象
    if (Object.prototype.hasOwnProperty.call(res, 'data') || res.data !== undefined) {
      return res.data
    }
    return res
  },
  async (error) => {
    const { config } = error

    clearConcurrencyEntry(config)

    const isAborted =
      error.code === 'ERR_CANCELED' ||
      error.name === 'AbortError' ||
      config?.signal?.aborted === true
    if (isAborted) {
      return Promise.reject(error)
    }

    if (config?.rawResponse) {
      return Promise.reject(error)
    }

    // 检查响应中的 code 字段，处理 token 过期
    if (error.response && error.response.data) {
      const res = error.response.data
      if (res.code === 'A00004') {
        // Token 过期，尝试刷新 token 后重试
        // handleTokenExpired 内部会重新发起请求并返回 Promise
        return handleTokenExpired(config)
      }
    }

    // 默认模式：统一错误处理（showError !== false 且去重）
    if (import.meta.client && shouldShowError(config)) {
      let message = '网络请求错误'
      if (error.response) {
        switch (error.response.status) {
          case 401:
            message = '未授权，请重新登录'
            break
          case 403:
            message = '拒绝访问'
            break
          case 404:
            message = '请求地址错误'
            break
          case 500:
            message = '服务器内部错误'
            break
          default:
            message = error.response.data?.message || '连接错误'
        }
      } else if (error.message.includes('timeout')) {
        message = '请求超时'
      }
      showErrorMessage(message)
    }
    return Promise.reject(error)
  }
)

/**
 * @description 处理 token 过期，刷新 token 后重试请求
 * @param {Object} originalRequest - 原始请求配置
 * @returns {Promise} 重新发起请求的 Promise
 */
async function handleTokenExpired(originalRequest) {
  // 如果请求配置中标记了不需要重试或跳过重试，直接拒绝
  if (originalRequest._retry || originalRequest._skipRetry) {
    if (import.meta.client) {
      const { handleUnauthorized } = useAuthLinks()
      await handleUnauthorized({ forceRedirect: true, redirectToLogin: true })
    }
    return Promise.reject(new Error('Token 刷新失败，请重新登录'))
  }

  // 等待 token 刷新完成（如果正在刷新，会加入队列等待）
  await handleTokenRefresh({ force: true })

  // 标记请求已重试，避免无限循环
  originalRequest._retry = true

  // 更新请求头中的 token（确保使用最新的 token）
  if (import.meta.client) {
    try {
      const userStore = useUserStore()
      if (userStore.accessToken) {
        // 确保使用最新的 token
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers['opentoken'] = userStore.accessToken
      } else {
        // 如果没有 token，说明刷新失败
        console.warn('[API] Token 刷新后仍无 accessToken')
        return Promise.reject(new Error('Token 刷新失败'))
      }
    } catch (e) {
      console.error('[API] 更新请求头 token 失败:', e)
      return Promise.reject(e)
    }
  }

  // 重新发起请求
  return api(originalRequest)
}

export default api
